import { Divider } from '@katalon-studio/katalon-ui/v2';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { LoadingProgress } from '../../../layout';
import PricingSection from '../../../layout/pricing-section/PricingSection';
import { checkout } from '../../../layout/routes';
import { Feature, OrderProductPreview, Subscription, SubscriptionSource } from '../../../models';
import { useNavigate, useQuery } from '../../../routes';
import { fromAccounts, fromOrder, fromSubscription, useAppDispatch } from '../../../store';
import { useGetTrackingCookies, usePlanCartSnapshot } from '../../../hook';
import { sendTrackingData } from '../../../utils/utils';
import { PreviewedProductListing } from './PreviewedProductListing';

const useStyles = makeStyles(({
  priceSummaryContainer: {
    backgroundColor: '#fff',
  },
}));

type QueryParam = {
  [key: string]: any; // 👈️ variable key
};

const PriceSummary = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const loading = useSelector(fromOrder.selectPreviewLoading);
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const netTotal = useSelector(fromOrder.selectSubTotalPrice) ?? 0;
  const discountTotal = useSelector(fromOrder.selectDiscountTotal) ?? 0;
  const orderPreviewRequest = useSelector(fromOrder.selectOrderPreviewRequest);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const productsPreview = [...orderPreviewResponse?.productsPreview ?? []]
    ?.sort((a, b) => (a.discountAmount > b.discountAmount ? -1 : 1));
  const subscriptions = useSelector((fromSubscription.selectByAccountId(accountId)));
  const isOptedInFirstThreeLicenses = useSelector(fromAccounts.selectIsOptedInFirstThreeLicenses);
  const [showOptInFirstThreeLicenses, setShowOptInFirstThreeLicenses] = useState(false);
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const previewCartRef = useRef<string | undefined>(undefined);

  const hasPendingOrder = pendingOrder && pendingOrder.length > 0;
  const hasKSESubscription = subscriptions.some(
    subscription => subscription.feature === Feature.PER_USER_KSE,
  );
  const hasPreviewKSE = productsPreview?.some(
    productPreview => productPreview.feature === Feature.PER_USER_KSE,
  ) ?? false;

  useEffect(() => {
    if (orderPreviewRequest === undefined
      || orderPreviewRequest.singleProductList.length === 0) {
      dispatch(fromOrder.resetOrderPreviewResponse());
      return;
    }

    dispatch(fromOrder.doPreviewOrder(orderPreviewRequest));
  }, [orderPreviewRequest]);

  const checkSwitchSubscriptionInterval = (
    subscription: Subscription,
    productPreview: OrderProductPreview,
  ) => (
    subscription.feature === productPreview.feature
    && subscription.source === SubscriptionSource.RECURLY
    && subscription.billingInterval !== productPreview.billingInterval
  );

  const generateParams = () => {
    const featureParam: QueryParam = {};
    orderPreviewResponse?.productsPreview.forEach(productPreview => {
      const prefix = Feature.getFeaturePrefix(productPreview.feature);
      featureParam[`${prefix}Quota`] = productPreview.newQuantity;
      featureParam[`${prefix}Cycle`] = productPreview.billingInterval;

      const haveTerminatedSubscription = subscriptions.find(
        subscription => checkSwitchSubscriptionInterval(subscription, productPreview),
      );
      if (haveTerminatedSubscription) {
        featureParam.terminated = `${featureParam.terminated}, ${productPreview.feature}`;
      }
    });

    return featureParam;
  };

  const generateTerminatedParams = () => {
    const featureParam: QueryParam = {};
    const terminatedProducts = orderPreviewResponse?.productsPreview
      .filter(productPreview => subscriptions.some(
        subscription => checkSwitchSubscriptionInterval(subscription, productPreview),
      ))
      .map(productPreview => productPreview.feature);

    if (terminatedProducts && terminatedProducts.length > 0) {
      featureParam.terminated = terminatedProducts.join(',');
      return featureParam;
    }

    return undefined;
  };

  const params = replaceQuery({
    accountId: `${accountId}`,
    ...generateParams(),
    ...generateTerminatedParams(),
  });

  const proceedToCheckout = () => {
    if (orderPreviewResponse) {
      const navigateToCheckout = async () => {
        navigate(checkout.path, params);
      };
      navigateToCheckout().catch(() => {
      });

      sendTrackingData(
        'payment_checkout_button_clicked',
        accountId,
        undefined,
        {
          field: 'plan_confirmation',
          object: 'button',
          action: 'proceed_to_checkout',
          currency: cartSnapshot.currency,
          value: cartSnapshot.value,
          items: cartSnapshot.items,
          ...trackingCookies,
        },
      );
    }
  };

  useEffect(() => {
    setShowOptInFirstThreeLicenses(isOptedInFirstThreeLicenses);
  }, [isOptedInFirstThreeLicenses]);

  useEffect(() => {
    if (cartSnapshot.items.length === 0) {
      return;
    }

    const serialized = JSON.stringify(cartSnapshot);
    if (previewCartRef.current === serialized) {
      return;
    }

    previewCartRef.current = serialized;

    sendTrackingData(
      'preview_cart',
      accountId,
      undefined,
      {
        page: 'expand_plan',
        currency: cartSnapshot.currency,
        value: cartSnapshot.value,
        items: cartSnapshot.items,
        ...trackingCookies,
      },
    );
  }, [accountId, cartSnapshot]);

  return (
    <Box
      id="expand-plan-price-summary"
      className={classes.priceSummaryContainer}
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Box
        id="expand-plan-price-summary-header"
        sx={theme => ({
          [theme.breakpoints.down('tabletL')]: {
            display: 'none',
          },
        })}
      >
        <Typography
          variant="body1"
          fontWeight={600}
          fontSize={21}
        >
          <FormattedMessage id="page.expand.plan.price.summary.header" />
        </Typography>
      </Box>
      <Box
        id="expand-plan-price-summary-product-list"
        display="flex"
        flexDirection="column"
        sx={theme => ({
          padding: theme.spacing(1, 2),
          overflow: 'scroll',
          minHeight: theme.spacing(40),
          height: '100%',
          [theme.breakpoints.down('tabletL')]: {
            display: 'none',
          },
        })}
      >
        {loading
          ? (<LoadingProgress />)
          : (
            <PreviewedProductListing productsPreview={productsPreview} />
          )}
      </Box>
      <Box
        id="expand-plan-price-summary-price"
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
        height="auto"
      >
        <Divider />
        <Box
          id="expand-plan-price-summary-net-price"
          display="block"
          height="auto"
        >
          <PricingSection
            subTotal={netTotal}
            totalDiscount={discountTotal}
            totalTax={0}
            totalPrice={netTotal - discountTotal}
            showOptInFirstThreeLicenses={showOptInFirstThreeLicenses}
            showTax={false}
            loading={loading}
          />
        </Box>
        <Box
          id="expand-plan-price-summary-desc"
          sx={theme => ({
            padding: theme.spacing(2, 0),
            [theme.breakpoints.down('tabletL')]: {
              padding: theme.spacing(1, 0),
            },
          })}
        >
          <Typography variant="body1" fontWeight={500} fontSize={14}>
            <FormattedMessage id="page.expand.plan.price.summary.desc" />
          </Typography>
        </Box>
        {hasPendingOrder && (
          <Box
            id="expand-plan-price-summary-alert"
            alignItems="center"
            alignContent="center"
            justifyContent="center"
          >
            <Alert
              id="expand-plan-price-summary-checkout-button-alert"
              severity="warning"
              sx={theme => ({
                marginBottom: 2,
                width: '100%',
                '& .MuiAlert-icon': {
                  margin: theme.spacing(1, 2, 1, 0),
                },
              })}
            >
              <FormattedMessage id="page.expand.plan.price.summary.pending.order.alert" />
            </Alert>
          </Box>
        )}
        <Box
          id="expand-plan-price-summary-checkout-button"
          display="flex"
        >
          <Button
            id="expand-plan-price-summary-checkout-button-action"
            variant="contained"
            disabled={hasPendingOrder || (!hasKSESubscription && !hasPreviewKSE)}
            onClick={proceedToCheckout}
            sx={theme => ({
              height: theme.spacing(5),
              backgroundColor: '#5959EB',
              marginBottom: theme.spacing(2),
              color: '#fff',
              '&:hover': {
                backgroundColor: '#43509B',
              },
            })}
            fullWidth
          >
            <FormattedMessage id="page.expand.plan.price.summary.checkout.button" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceSummary;
